import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Science as ScienceIcon,
  Public as NetworkIcon,
  Euro as EuroIcon,
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh as EnhanceIcon,
  Groups as GroupsIcon,
  Star as StarIcon
} from '@mui/icons-material';

const GoldCompatibilitySection = ({ requestId, requestData }) => {
  const [goldData, setGoldData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enhancementDialog, setEnhancementDialog] = useState(false);
  const [selectedEnhancement, setSelectedEnhancement] = useState(null);
  const [applyingEnhancement, setApplyingEnhancement] = useState(false);

  useEffect(() => {
    loadGoldAnalysis();
  }, [requestId]);

  const loadGoldAnalysis = async () => {
    try {
      const response = await fetch(`http://localhost:3002/api/feasibility/${requestId}/gold-analysis`);
      const data = await response.json();
      setGoldData(data);
    } catch (error) {
      console.error('Error loading GOLD analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.9) return 'success';
    if (score >= 0.8) return 'info';
    if (score >= 0.7) return 'warning';
    return 'error';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent':
      case 'very_good':
        return <CheckIcon color="success" />;
      case 'good':
        return <CheckIcon color="info" />;
      case 'moderate':
        return <WarningIcon color="warning" />;
      default:
        return <WarningIcon color="error" />;
    }
  };

  const renderStarRating = (score) => {
    const stars = Math.round(score * 5);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {[...Array(5)].map((_, i) => (
          <StarIcon 
            key={i} 
            sx={{ 
              color: i < stars ? '#ffd700' : '#e0e0e0',
              fontSize: 20 
            }} 
          />
        ))}
      </Box>
    );
  };

  const applyEnhancement = async (enhancement) => {
    setApplyingEnhancement(true);
    try {
      const response = await fetch(`http://localhost:3002/api/feasibility/${requestId}/apply-enhancement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enhancement_id: enhancement.category })
      });
      
      const result = await response.json();
      if (result.success) {
        // Refresh GOLD analysis data
        await loadGoldAnalysis();
        setEnhancementDialog(false);
        alert('🎉 Enhancement successfully applied!\n\nYour study is now GOLD Premium Tier eligible.');
      }
    } catch (error) {
      console.error('Error applying enhancement:', error);
    } finally {
      setApplyingEnhancement(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrophyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">GOLD-Standard Compatibility Analysis</Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (!goldData) {
    return null;
  }

  return (
    <Box>
      {/* Main GOLD Compatibility Card */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrophyIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                🏆 GOLD-Standard Compatibility Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {requestData?.title || 'Current Research Request'}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: getScoreColor(goldData.overall_score) + '.main' }}>
                  {Math.round(goldData.overall_score * 100)}%
                </Typography>
                <Typography variant="h6" gutterBottom>
                  GOLD Compatibility Score
                </Typography>
                {renderStarRating(goldData.overall_score)}
              </Box>

              <Alert 
                severity={goldData.vision_zero_eligible ? 'success' : 'warning'} 
                sx={{ mb: 2 }}
              >
                <strong>Vision Zero Consortium Status:</strong><br />
                {goldData.vision_zero_eligible ? 
                  '✅ ELIGIBLE (Score ≥ 80%) - Ready for European network access' : 
                  '⚠️ ENHANCEMENT NEEDED - 80% threshold required for consortium access'
                }
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Breakdown by Domain:
              </Typography>
              
              {Object.entries(goldData.breakdown).map(([domain, data]) => (
                <Box key={domain} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getStatusIcon(data.status)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {domain.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${Math.round(data.score * 100)}%`} 
                      size="small" 
                      color={getScoreColor(data.score)}
                    />
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={data.score * 100} 
                    sx={{ mt: 0.5, height: 4 }}
                    color={getScoreColor(data.score)}
                  />
                </Box>
              ))}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhancement Suggestions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EnhanceIcon sx={{ mr: 2, color: 'secondary.main' }} />
            <Typography variant="h6">💡 Smart Enhancement Suggestions</Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Upgrade your study to GOLD Premium Tier ({Math.round(goldData.overall_score * 100)}% → 96%):</strong>
          </Alert>

          {goldData.enhancement_suggestions.map((enhancement, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ScienceIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {enhancement.category === 'biomarker_upgrade' ? '🧬' : '📊'} {enhancement.title}
                    </Typography>
                  </Box>
                  <Chip 
                    label={`+€${enhancement.value_increase.toLocaleString()}`} 
                    color="success" 
                    size="small" 
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    label={`${enhancement.implementation_effort} effort`} 
                    color="info" 
                    size="small"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {enhancement.description}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>Required Additions:</Typography>
                <List dense>
                  {enhancement.required_additions.map((addition, idx) => (
                    <ListItem key={idx} sx={{ py: 0 }}>
                      <ListItemIcon><CheckIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                      <ListItemText primary={addition} />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText">
                    <strong>Impact:</strong> {enhancement.consortium_impact}
                  </Typography>
                  <Typography variant="body2" color="success.contrastText">
                    <strong>New GOLD Score:</strong> {Math.round(enhancement.new_gold_score * 100)}%
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<EnhanceIcon />}
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setSelectedEnhancement(enhancement);
                    setEnhancementDialog(true);
                  }}
                >
                  Add to Study
                </Button>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Business Impact Summary */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'primary.light', color: 'white' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <EuroIcon sx={{ mr: 1 }} /> Total Enhancement Potential
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">Standard Study Value:</Typography>
                <Typography variant="h6">€{goldData.business_impact.current_value.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">+ GOLD Enhancement:</Typography>
                <Typography variant="h6">€{(goldData.business_impact.gold_enhanced_potential - goldData.business_impact.current_value).toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">+ Consortium Premium:</Typography>
                <Typography variant="h6">€{goldData.business_impact.consortium_premium_potential.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>= Total Potential:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  €{goldData.business_impact.total_enhanced_value.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  ({Math.round(goldData.business_impact.total_enhanced_value / goldData.business_impact.current_value * 10) / 10}x increase)
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                sx={{ bgcolor: 'white', color: 'primary.main', mr: 1 }}
                startIcon={<EnhanceIcon />}
              >
                Apply All Enhancements
              </Button>
              <Button 
                variant="outlined" 
                sx={{ borderColor: 'white', color: 'white' }}
              >
                Customize Selection
              </Button>
            </Box>
          </Paper>
        </CardContent>
      </Card>

      {/* Network Opportunities */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NetworkIcon sx={{ mr: 2, color: 'info.main' }} />
            <Typography variant="h6">🌐 Vision Zero Network Opportunities</Typography>
          </Box>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            Your {goldData.vision_zero_eligible ? 'GOLD-enhanced' : 'enhanced'} study qualifies for:
          </Alert>

          {goldData.network_opportunities.map((opportunity, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'info.light' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.contrastText' }}>
                🔬 {opportunity.study_name}
              </Typography>
              <Typography variant="body2" color="info.contrastText">
                {opportunity.participating_countries} countries • {opportunity.collaboration_type}
              </Typography>
              <Typography variant="body2" color="info.contrastText" sx={{ mt: 1 }}>
                <strong>Additional revenue potential: €{opportunity.additional_revenue_potential.toLocaleString()}</strong>
              </Typography>
            </Paper>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<GroupsIcon />} sx={{ mr: 1 }}>
              Explore Network Studies
            </Button>
            <Button variant="outlined" startIcon={<NetworkIcon />}>
              Contact Collaborators
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Enhancement Application Dialog */}
      <Dialog open={enhancementDialog} onClose={() => setEnhancementDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Apply Enhancement: {selectedEnhancement?.title}
        </DialogTitle>
        <DialogContent>
          {selectedEnhancement && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedEnhancement.description}
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                This enhancement will increase your study value by <strong>€{selectedEnhancement.value_increase.toLocaleString()}</strong> 
                and improve your GOLD compatibility score to <strong>{Math.round(selectedEnhancement.new_gold_score * 100)}%</strong>.
              </Alert>

              <Typography variant="subtitle2" gutterBottom>Required additions:</Typography>
              <List>
                {selectedEnhancement.required_additions.map((addition, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary={addition} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnhancementDialog(false)} disabled={applyingEnhancement}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => applyEnhancement(selectedEnhancement)}
            disabled={applyingEnhancement}
            startIcon={applyingEnhancement ? <LinearProgress size={20} /> : <EnhanceIcon />}
          >
            {applyingEnhancement ? 'Applying...' : 'Apply Enhancement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoldCompatibilitySection;